import { put, all, takeLatest, select } from "redux-saga/effects";
import * as actionTypes from "./actionTypes";
import * as extractionsActionTypes from "../extractions/actionTypes";
import API from "../../commonUtils/API.js";
import { toast } from "react-toastify";
import { handleApiError } from "../../commonUtils/Functions";
import { parseBackendErrors } from "../../commonUtils/queryBuilder.helper.js";
import { setQBErrors } from "../extractions/action";


function* getExtExclusionsSaga({ params }) {
    const { extId, keyword, versionId, is_draft } = params;
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        let queryString = '';
        if (keyword)              queryString += `&keyword=${encodeURIComponent(keyword)}`;
        if (versionId)            queryString += `&versionId=${versionId}`;
        if (is_draft !== undefined && is_draft !== null) queryString += `&is_draft=${is_draft}`;

        const { data } = yield API.get(`/ext/${extId}/exclusions?${queryString}`);
        if (data.success) {
            yield put({ type: actionTypes.GET_EXT_EXCLUSIONS_SUCCESS, payload: data.data });
        } else {
            toast.error("Oops! Something went unexpectedly wrong.");
            yield put({ type: actionTypes.GET_EXT_EXCLUSIONS_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Oops! Something went unexpectedly wrong.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.GET_EXT_EXCLUSIONS_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* getExtExclusionSaga({ id }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const extractionData = yield select((state) => state.Extractions.extractionData);

        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.GET_EXT_EXCLUSION_FAILURE, 
                payload: { message: "Extraction ID not found" } 
            });
            return;
        }

        const { data } = yield API.get(`/ext/${extractionData.id}/exclusions/${id}`);
        if (data.success) {
            yield put({ type: actionTypes.GET_EXT_EXCLUSION_SUCCESS, payload: data.data });
        } else {
            toast.error("Could not load exclusion.");
            yield put({ type: actionTypes.GET_EXT_EXCLUSION_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Could not load exclusion.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.GET_EXT_EXCLUSION_FAILURE, payload: data || { message: "Internal server error" } });
    }
}

function* createExtExclusionSaga({ payload }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });
        
        const { formData } = payload;
        const extractionData = yield select((state) => state.Extractions.extractionData);
        
        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.CREATE_EXT_EXCLUSION_FAILURE, 
                payload: { message: "Extraction ID not found" } 
            });
            return;
        }

        const { data } = yield API.post(`/ext/${extractionData.id}/exclusions`, formData);
        
        if (data.success) {
            yield put(setQBErrors({}));
            yield put({ 
                type: actionTypes.CREATE_EXT_EXCLUSION_SUCCESS, 
                payload: data.data 
            });

            // Read filters from ExtExclusions slice, not Extractions
            const filters = yield select((state) => state.ExtExclusions.filters);
            yield put({ type: actionTypes.GET_EXT_EXCLUSIONS, params: { extId: extractionData?.id, ...filters } });
        } else {
            if (data?.data) {
                const parsedErrors = parseBackendErrors(data.data);
                yield put(setQBErrors(parsedErrors));
                toast.error("Please fix validation errors");
            } else {
                toast.error(data?.message || "Could not save exclusion.");
            }
            yield put({ 
                type: actionTypes.CREATE_EXT_EXCLUSION_FAILURE, 
                payload: data 
            });
        }

    } catch (error) {
        const errorData = error?.response?.data;
        if (errorData?.data) {
            const parsedErrors = parseBackendErrors(errorData.data);
            yield put(setQBErrors(parsedErrors));
            toast.error("Please fix validation errors");
        } else {
            handleApiError(error, "Could not save exclusion.");
        }
        yield put({ 
            type: actionTypes.CREATE_EXT_EXCLUSION_FAILURE, 
            payload: { 
                message: errorData?.message || "Internal server error",
                errors: errorData?.data || null
            } 
        });
    }
};

function* updateExtExclusionSaga({ payload }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });
        
        const { id, formData } = payload;
        const extractionData = yield select((state) => state.Extractions.extractionData);

        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.UPDATE_EXT_EXCLUSION_FAILURE, 
                payload: { message: "Extraction ID not found" } 
            });
            return;
        }
        
        const { data } = yield API.put(`/ext/${extractionData.id}/exclusions/${id}`, formData);
        
        if (data.success) {
            yield put(setQBErrors({}));
            yield put({ 
                type: actionTypes.UPDATE_EXT_EXCLUSION_SUCCESS, 
                payload: data.data 
            });

            // Read filters from ExtExclusions slice, not Extractions
            const filters = yield select((state) => state.ExtExclusions.filters);
            yield put({ type: actionTypes.GET_EXT_EXCLUSIONS, params: { extId: extractionData?.id, ...filters } });
        } else {
            if (data?.data) {
                const parsedErrors = parseBackendErrors(data.data);
                yield put(setQBErrors(parsedErrors));
                toast.error("Please fix validation errors");
            } else {
                toast.error(data?.message || "Could not save updated exclusion data.");
            }
            yield put({ 
                type: actionTypes.UPDATE_EXT_EXCLUSION_FAILURE, 
                payload: data 
            });
        }

    } catch (error) {
        const errorData = error?.response?.data;
        if (errorData?.data) {
            const parsedErrors = parseBackendErrors(errorData.data);
            yield put(setQBErrors(parsedErrors));
            toast.error("Please fix validation errors");
        } else {
            handleApiError(error, "Could not save updated exclusion data.");
        }
        yield put({ 
            type: actionTypes.UPDATE_EXT_EXCLUSION_FAILURE, 
            payload: { 
                message: errorData?.message || "Internal server error",
                errors: errorData?.data || null
            } 
        });
    }
};

function* updateExtExclusionsOrderSaga({ payload }) {
    const extractionData = yield select((state) => state.Extractions.extractionData);
    const filters = yield select((state) => state.ExtExclusions.filters);
    try {
        const { orderData } = payload;

        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.UPDATE_EXT_EXCLUSIONS_ORDER_FAILURE, 
                payload: { message: "Extraction ID not found" } 
            });
            return;
        }

        const { data } = yield API.put(
            `/ext/${extractionData.id}/exclusions/update-order`, 
            { orderData }
        );

        if (data.success) {
            yield put({ 
                type: actionTypes.UPDATE_EXT_EXCLUSIONS_ORDER_SUCCESS, 
                payload: data.data 
            });
        } else {
            toast.error(data?.message || "Could not update order.");
            yield put({ 
                type: actionTypes.UPDATE_EXT_EXCLUSIONS_ORDER_FAILURE, 
                payload: data 
            });
        }

    } catch (error) {
        handleApiError(error, "Could not update order.");
        const data = error?.response?.data;
        toast.error("Failed to update order. Refreshing...");
        yield put({ type: actionTypes.GET_EXT_EXCLUSIONS, params: { extId: extractionData?.id, ...filters } });
        yield put({ 
            type: actionTypes.UPDATE_EXT_EXCLUSIONS_ORDER_FAILURE, 
            payload: data || { message: "Internal server error" } 
        });
    }
};

function* deleteExtExclusionSaga({ Id }) {
    try {
        const extractionData = yield select((state) => state.Extractions.extractionData);
        const filters = yield select((state) => state.ExtExclusions.filters);
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const { data } = yield API.delete(`/ext/${extractionData.id}/exclusions/${Id}`);

        if (data.success) {
            yield put({ type: actionTypes.DELETE_EXT_EXCLUSION_SUCCESS, payload: data });
            yield put({ type: actionTypes.GET_EXT_EXCLUSIONS, params: { extId: extractionData?.id, ...filters } });
        } else {
            toast.error(data?.message || "Could not delete exclusion.");
            yield put({ type: actionTypes.DELETE_EXT_EXCLUSION_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Could not delete exclusion.");
        const data = error?.response?.data;
        toast.error(data?.message || "Could not delete exclusion.");
        yield put({ type: actionTypes.DELETE_EXT_EXCLUSION_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* setOpenModalSaga({ payload }) {
    if (!payload) {
        yield put({ type: extractionsActionTypes.RESET_QUERY_BUILDER });
    }
}

function* ExtExclusionsSaga() {
  yield all([
    takeLatest(actionTypes.GET_EXT_EXCLUSIONS, getExtExclusionsSaga),
    takeLatest(actionTypes.GET_EXT_EXCLUSION, getExtExclusionSaga),
    takeLatest(actionTypes.CREATE_EXT_EXCLUSION, createExtExclusionSaga),
    takeLatest(actionTypes.UPDATE_EXT_EXCLUSION, updateExtExclusionSaga),
    takeLatest(actionTypes.UPDATE_EXT_EXCLUSIONS_ORDER, updateExtExclusionsOrderSaga),
    takeLatest(actionTypes.DELETE_EXT_EXCLUSION, deleteExtExclusionSaga),
    takeLatest(actionTypes.SET_OPEN_MODAL, setOpenModalSaga)
  ]);
}

export default ExtExclusionsSaga;